import { Fragment } from "react";
import { CloseIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { Drawer } from "@design-system/Drawer/Drawer";
import styles from "./ReconciliationDrawer.module.scss";

export interface ReconciliationDrawerStep {
    number: number;
    total: number;
    action: string;
    details?: string[];
}

interface ReconciliationDrawerProps {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    closeLabel: string;
    subtitle?: React.ReactNode;
    step?: ReconciliationDrawerStep;
    isBusy?: boolean;
    footer?: React.ReactNode;
    children: React.ReactNode;
}

export const ReconciliationDrawer: React.FC<ReconciliationDrawerProps> = ({
    open,
    onClose,
    title,
    closeLabel,
    subtitle,
    step,
    isBusy = false,
    footer,
    children,
}) => (
    <Drawer
        open={open}
        onClose={onClose}
        size="fullScreen"
        noPadding
        renderHeader={false}
        dismissible={!isBusy}
    >
        <div className={styles.workflow}>
            <header className={styles.header}>
                <div className={styles.titleRow}>
                    <div className={styles.titleGroup}>
                        <h1 className={styles.title}>{title}</h1>
                        {subtitle && (
                            <span className={styles.subtitle}>{subtitle}</span>
                        )}
                    </div>
                    <Button
                        variant="secondary"
                        iconOnly
                        size="sm"
                        aria-label={closeLabel}
                        className={styles.closeButton}
                        disabled={isBusy}
                        onClick={onClose}
                    >
                        <CloseIcon />
                    </Button>
                </div>
                {step && (
                    <div className={styles.stepRow}>
                        <div className={styles.stepIndicator}>
                            <span>
                                Step {step.number} of {step.total}
                            </span>
                            {[step.action, ...(step.details ?? [])].map(
                                (item, index) => (
                                    <Fragment key={`${index}-${item}`}>
                                        <span
                                            className={styles.stepSeparator}
                                            aria-hidden
                                        >
                                            •
                                        </span>
                                        <span>{item}</span>
                                    </Fragment>
                                ),
                            )}
                        </div>
                    </div>
                )}
            </header>
            <div className={styles.content}>{children}</div>
            {footer}
        </div>
    </Drawer>
);
